import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import '../AdminServiceEditor/AdminServiceEditor.css';

const AdminBlogEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';

    const [formData, setFormData] = useState({
        title: '',
        excerpt: '',
        content: '',
        author: '',
        image_url: '',
        thumb_url: '',
        tags: [],
        is_published: false
    });
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [newTag, setNewTag] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('pcic_admin_token');
        if (!token) {
            navigate('/admin');
            return;
        }

        if (!isNew) {
            fetchBlog();
        }
    }, [id, isNew, navigate]);

    const fetchBlog = async () => {
        try {
            const res = await fetch(`/api/blogs/${id}`);
            const data = await res.json();
            if (data.tags) {
                data.tags = JSON.parse(data.tags);
            }
            data.is_published = Boolean(data.is_published);
            setFormData(data);
        } catch (error) {
            alert('Error loading blog');
            navigate('/admin/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const addTag = () => {
        if (newTag.trim()) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim()]
            }));
            setNewTag('');
        }
    };

    const removeTag = (index) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const url = isNew ? '/api/blogs' : `/api/blogs/${id}`;
            const method = isNew ? 'POST' : 'PUT';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                navigate('/admin/dashboard');
            } else {
                const error = await res.json();
                alert(error.message || 'Error saving blog');
            }
        } catch (error) {
            alert('Error saving blog');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="admin-loading">Loading...</div>;
    }

    return (
        <div className="admin-editor">
            <header className="editor-header">
                <Link to="/admin/dashboard" className="back-link">← Back to Dashboard</Link>
                <h1>{isNew ? 'Add New Blog' : 'Edit Blog'}</h1>
            </header>

            <form onSubmit={handleSubmit} className="editor-form">
                <div className="form-group">
                    <label>Title *</label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div className="form-group">
                    <label>Author</label>
                    <input
                        type="text"
                        name="author"
                        value={formData.author}
                        onChange={handleChange}
                    />
                </div>

                <div className="form-group">
                    <label>Excerpt</label>
                    <textarea
                        name="excerpt"
                        value={formData.excerpt}
                        onChange={handleChange}
                        rows={3}
                        placeholder="Brief summary of the blog post"
                    />
                </div>

                <div className="form-group">
                    <label>Content</label>
                    <textarea
                        name="content"
                        value={formData.content}
                        onChange={handleChange}
                        rows={12}
                    />
                </div>

                <div className="form-group">
                    <label>Image URL</label>
                    <input
                        type="text"
                        name="image_url"
                        value={formData.image_url}
                        onChange={handleChange}
                    />
                </div>

                <div className="form-group">
                    <label>Thumbnail URL</label>
                    <input
                        type="text"
                        name="thumb_url"
                        value={formData.thumb_url}
                        onChange={handleChange}
                    />
                </div>

                <div className="form-group">
                    <label>Tags</label>
                    <div className="features-list">
                        {formData.tags.map((tag, index) => (
                            <div key={index} className="feature-item">
                                <span>{tag}</span>
                                <button type="button" onClick={() => removeTag(index)}>×</button>
                            </div>
                        ))}
                    </div>
                    <div className="feature-add">
                        <input
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            placeholder="Add a tag"
                        />
                        <button type="button" onClick={addTag}>Add</button>
                    </div>
                </div>

                <div className="form-group checkbox-group">
                    <input
                        type="checkbox"
                        name="is_published"
                        id="is_published"
                        checked={formData.is_published}
                        onChange={handleChange}
                    />
                    <label htmlFor="is_published">Published</label>
                </div>

                <div className="form-actions">
                    <button type="submit" disabled={saving} className="save-btn">
                        {saving ? 'Saving...' : 'Save Blog'}
                    </button>
                    <Link to="/admin/dashboard" className="cancel-btn">Cancel</Link>
                </div>
            </form>
        </div>
    );
};

export default AdminBlogEditor;
